import { useTranslation } from 'react-i18next';
import { ArrowRight, Building2, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const EcosystemSection = () => {
  const { t } = useTranslation(['common', 'about']);

  const projects = [
    {
      name: t('about:digitalEcosystem.projects.bidmio.name'),
      badge: t('about:digitalEcosystem.projects.bidmio.market'),
      badgeColor: 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30',
      description: t('about:digitalEcosystem.projects.bidmio.description'),
      target: t('about:digitalEcosystem.projects.bidmio.audience'),
      url: t('about:digitalEcosystem.projects.bidmio.url'),
      icon: Building2,
      gradient: 'from-amber-500/5 to-orange-500/5',
      iconBg: 'from-amber-500/20 to-orange-500/20',
      iconBorder: 'border-amber-500/30',
      iconColor: 'text-amber-600 dark:text-amber-500',
      targetBg: 'bg-amber-500/10',
      targetBorder: 'border-amber-500/20',
      targetText: 'text-amber-700 dark:text-amber-400',
      buttonGradient: 'from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700',
      hoverShadow: 'hover:shadow-amber-500/10'
    },
    {
      name: t('about:digitalEcosystem.projects.vibecoder.name'),
      badge: t('about:digitalEcosystem.projects.vibecoder.market'),
      badgeColor: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
      description: t('about:digitalEcosystem.projects.vibecoder.description'),
      target: t('about:digitalEcosystem.projects.vibecoder.audience'),
      url: t('about:digitalEcosystem.projects.vibecoder.url'),
      icon: Code,
      gradient: 'from-emerald-500/5 to-teal-500/5',
      iconBg: 'from-emerald-500/20 to-teal-500/20',
      iconBorder: 'border-emerald-500/30',
      iconColor: 'text-emerald-600 dark:text-emerald-500',
      targetBg: 'bg-emerald-500/10',
      targetBorder: 'border-emerald-500/20',
      targetText: 'text-emerald-700 dark:text-emerald-400',
      buttonGradient: 'from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700',
      hoverShadow: 'hover:shadow-emerald-500/10'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-background via-muted/30 to-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-white/5 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t('about:digitalEcosystem.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('about:digitalEcosystem.intro')}
          </p>
        </div>

        {/* Projects - Compact Card Layout */}
        <div className="max-w-5xl mx-auto space-y-10">
          {projects.map((project, index) => {
            const Icon = project.icon;
            
            return (
              <div 
                key={project.name}
                className={`group relative bg-gradient-to-br ${project.gradient} border ${project.iconBorder} rounded-2xl p-8 hover:shadow-xl ${project.hoverShadow} transition-all duration-300`}
                data-testid={`ecosystem-project-${index}`}
              >
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Small Icon */}
                  <div className="flex-shrink-0">
                    <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${project.iconBg} border ${project.iconBorder} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-10 h-10 ${project.iconColor}`} strokeWidth={2} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-2xl md:text-3xl font-bold">{project.name}</h3>
                      <Badge className={`${project.badgeColor} text-xs`}>
                        {project.badge}
                      </Badge>
                    </div>

                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      {project.description}
                    </p>

                    <div className={`${project.targetBg} border ${project.targetBorder} rounded-lg p-3 inline-block`}>
                      <p className="text-sm">
                        <span className={`font-semibold ${project.targetText}`}>{t('about:digitalEcosystem.targetAudience')}</span>{' '}
                        <span className="text-foreground/90">{project.target}</span>
                      </p>
                    </div>

                    <div>
                      <Button 
                        asChild 
                        className={`group/btn bg-gradient-to-r ${project.buttonGradient} text-white`}
                      >
                        <a 
                          href={project.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          data-testid={`link-${index === 0 ? 'bidmio' : 'vibecoder'}`}
                        >
                          {t('about:digitalEcosystem.visitButton')} {project.name}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('about:digitalEcosystem.closingMessage')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default EcosystemSection;
